// src/context/ToastContext.jsx
// App-wide, lightweight notifications. Any page can call `toast.success(...)`
// to tell the shopper something happened (order placed, item wishlisted, …).
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaHeart, FaTimes } from 'react-icons/fa';
import './Toast.css';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Never crash a page just because it rendered outside the provider.
  return ctx || { show: () => {}, success: () => {}, error: () => {}, info: () => {}, wish: () => {} };
};

const ICONS = {
  success: <FaCheckCircle />,
  error: <FaExclamationCircle />,
  info: <FaInfoCircle />,
  wish: <FaHeart />,
};

let seq = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message, options = {}) => {
    const { type = 'info', title = '', duration = 4000 } = options;
    const id = ++seq;
    setToasts((prev) => [...prev, { id, message, title, type }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const value = useMemo(() => ({
    show,
    dismiss,
    success: (message, opts = {}) => show(message, { ...opts, type: 'success' }),
    error: (message, opts = {}) => show(message, { ...opts, type: 'error' }),
    info: (message, opts = {}) => show(message, { ...opts, type: 'info' }),
    wish: (message, opts = {}) => show(message, { ...opts, type: 'wish', duration: 2500 }),
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.type}`}>
            <span className="toast-icon">{ICONS[t.type] || ICONS.info}</span>
            <div className="toast-body">
              {t.title && <strong className="toast-title">{t.title}</strong>}
              <span className="toast-message">{t.message}</span>
            </div>
            <button className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <FaTimes />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
